import { IAction } from 'app/types/IAction';
import { IDataFetchOptions } from 'app/types/List';
import { SortDirection } from 'constants/SortDirection';
import { action, makeObservable, runInAction } from 'mobx';
import { destroy, get, patch, post } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import BaseListStore from 'stores/BaseListStore';
import ResponseOverrideStore, { IResponseOverride } from './responseOverrideStore';

const SEARCH_FIELDS = ['name'];

export default class ResponseOverrideListStore extends BaseListStore<ResponseOverrideStore> {
    private readonly baseRoute = 'responseOverride';

    constructor() {
        super({
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC,
            itemStore: ResponseOverrideStore
        });

        makeObservable(this, {
            getResponseOverride: action,
            updateResponseOverride: action,
            addResponseOverride: action
        });
    }

    async getResponseOverride(args: IDataFetchOptions = {}, policyId: string): Promise<void> {
        const shouldReset = this._updateListState(args);

        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                limit: args.limit || this.limit,
                skip: this.skip,
                where: conditions.concat({ policyId })
            }
        };

        const { data } = await get('ResponseOverrideList', options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);

        runInAction(() => {
            this.count = data.length;
        });
    }

    async updateResponseOverride(responseOverride: IResponseOverride) {
        const { data } = await patch(`${this.baseRoute}/${responseOverride.id}`, responseOverride);
        const index = this.ids.indexOf(responseOverride.id);
        this.items[index].update(data);
    }

    async updateBulkResponseOverride(actions: IAction, policyId: string) {
        const ids = this.ids.map((id) => id);

        try {
            await post(`${this.baseRoute}/bulk-update`, { ids, actions });
            await this.getResponseOverride({ filter: this.filter }, policyId);
        } catch (e) {
            return e;
        }
    }

    async addResponseOverride(ids: string[], policyId: string) {
        try {
            const { data } = await post(`${this.baseRoute}/add-overrides`, { policyId, ids });

            if (data.success) {
                await this.getResponseOverride({}, policyId);
            }

            return data;
        } catch (e) {
            return e;
        }
    }

    async deleteResponseOverride(id: string) {
        await destroy(`${this.baseRoute}/${id}`);
        this.clearItem(id);
    }
}
